import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../components/hooks/useAuth";
import { Disclosure } from "@headlessui/react";
import Logo from "../components/views/Logo";
import MainMenu from "../components/menus/Main";
import ProfileMenu from "../components/menus/Profile";
import MobileMenuButton from "../components/buttons/MobileMenu";
import MobileMenu from "../components/menus/Mobile";
import NotificationBell from "@/components/navigation/NotificationBell";
import Breadcrumb from "../components/Breadcrumb";
import { useOutletContext, useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import ActionBar from "../components/mobile/ActionBar";
import { useState } from "react";

// ...

type BreadcrumbItem = { label: string; to?: string };
type BreadcrumbContext = { title?: string; trail?: BreadcrumbItem[] };

export default function MainLayout() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [showCamera, setShowCamera] = useState(false);

  // Handle photo upload from gallery
  const handlePhotoUpload = (file: File) => {
    // Create a FormData object to send the file
    const formData = new FormData();
    formData.append("file", file);

    // Here you would typically send this to your API
    console.log("Photo selected:", file.name);

    // Navigate to the wardrobe item creation page with the file
    // You might want to store the file in state or context instead
    navigate("/wardrobe/new", { state: { uploadedFile: file } });
  };

  // Handle camera capture
  const handlePhotoCapture = () => {
    setShowCamera(true);
    // In a real implementation, you might open a camera component
    // or use the device's native camera functionality

    // For now, let's just use the file input as a fallback
    console.log("Camera capture requested");
  };

  if (!token) {
    return <Navigate to="/login" />;
  }
  // prevent content flash while user loads
  if (token && !user) return null;
  // Enforce onboarding before accessing main app
  if (token && user && user.onboarded === false) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-gray-900 dark:text-gray-100">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 bg-gradient-to-br from-pink-500/2 via-purple-500/2 to-indigo-500/2 pointer-events-none" />

      <Disclosure
        as="nav"
        className="relative border-b border-gray-200/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg z-40"
      >
        {({ open }) => (
          <>
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/3 via-purple-500/3 to-indigo-500/3 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
              <div className="flex h-20 justify-between items-center">
                <div className="flex items-center gap-8">
                  <Logo />
                  <MainMenu />
                </div>

                <div className="hidden sm:ml-6 sm:flex sm:items-center gap-6">
                  {/* Elegant Add Items button */}
                  <button
                    type="button"
                    className="group relative rounded-full inline-flex items-center px-6 py-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-sm font-light tracking-wider uppercase text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 border border-white/20 backdrop-blur-sm overflow-hidden"
                    onClick={() => navigate("/wardrobe/new")}
                    aria-label="Add items"
                  >
                    <PlusIcon className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
                    <span className="relative z-10">Add Items</span>

                    {/* Enhanced gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Elegant shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-full group-hover:-translate-x-full transition-transform duration-700" />
                  </button>

                  {/* Profile dropdown */}
                  <ProfileMenu />
                  <NotificationBell />
                </div>

                <div className="-mr-2 flex items-center sm:hidden">
                  {/* Mobile menu button */}
                  <MobileMenuButton open={open} />
                </div>
              </div>

              {/* Decorative gradient lines */}
              <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent opacity-50" />
            </div>

            <MobileMenu />
          </>
        )}
      </Disclosure>

      {/* Main content area with luxury styling */}
      <div className="relative flex-1 container mx-auto max-w-7xl px-6 lg:px-8 py-8 mt-8 mb-24 sm:mb-8">
        {/* Subtle content background */}
        <div className="absolute inset-0 bg-white/30 dark:bg-gray-900/30 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl" />

        {/* Content wrapper */}
        <div className="relative z-10">
          <Outlet />
        </div>
      </div>

      {/* Mobile Action Bar */}
      <ActionBar />
    </div>
  );
}
